import React, { useEffect, useState } from 'react';
import { auth } from '../../../firebase/config';
import Button from '../../../components/Button';
import Toast from '../../../components/Toast';
import { FiPlus, FiSearch, FiEdit } from 'react-icons/fi';
import Icon from '../../../components/Icon';
import AddDeploymentModal from '../components/AddDeploymentModal/index';
import EditDeploymentModal from '../components/EditDeploymentModal/index';
import { fetchDeploymentsData, type Deployment } from '../service/deploymentsService';
import Table, { Column } from '../../../components/Table';

export default function Deployments() {
    const [deployments, setDeployments] = useState<Deployment[]>([]);
    const [filtered, setFiltered] = useState<Deployment[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });
    const [selectedDeployment, setSelectedDeployment] = useState<Deployment | null>(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
    const [companyTypes, setCompanyTypes] = useState<string[]>([]);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso'
    const isBankUser = userRole === 'bank';

    useEffect(() => {
        if (!uid) return;

        const loadData = async () => {
            setLoading(true);
            try {
                const deploymentsData = await fetchDeploymentsData(uid, isBankUser);
                setDeployments(deploymentsData);
                setFiltered(deploymentsData);

                // Extract unique company types
                const typesSet = new Set<string>();
                deploymentsData.forEach(deployment => {
                    if (deployment.company?.companyType) {
                        typesSet.add(deployment.company.companyType);
                    }
                });
                setCompanyTypes(Array.from(typesSet));
            } catch (err: any) {
                showToast('Failed to load deployments data', 'error');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [uid, isBankUser]);

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };

    useEffect(() => {
        let list = deployments;

        // Apply filter based on selected company type
        if (selectedFilter && selectedFilter !== 'All') {
            list = list.filter(deployment => deployment.company?.companyType === selectedFilter);
        }

        // Apply search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(deployment => {
                const branchCode = (deployment.branch?.branchCode || '').toString().toLowerCase();
                const branchAddress = (deployment.branch?.address || '').toString().toLowerCase();
                const companyName = (deployment.company?.companyName || '').toString().toLowerCase();
                const companyId = (deployment.company?.companyId || '').toString().toLowerCase();
                return branchCode.includes(term) || branchAddress.includes(term) ||
                    companyName.includes(term) || companyId.includes(term);
            });
        }

        setFiltered(list);
        setCurrentPage(1);
    }, [searchTerm, deployments, selectedFilter]);

    const handleReset = () => {
        setSearchTerm('');
        setSelectedFilter(null);
        setFiltered(deployments);
        setCurrentPage(1);
    };

    const openEditModal = (deployment: Deployment) => {
        setSelectedDeployment(deployment);
        setShowEditModal(true);
    };

    const handleDeploymentAdded = async () => {
        if (!uid) return;
        setLoading(true);
        try {
            const deploymentsData = await fetchDeploymentsData(uid, isBankUser);
            setDeployments(deploymentsData);

            // Extract unique company types
            const typesSet = new Set<string>();
            deploymentsData.forEach(d => {
                if (d.company?.companyType) {
                    typesSet.add(d.company.companyType);
                }
            });
            setCompanyTypes(Array.from(typesSet));

            setCurrentPage(1);
            showToast('✅ Deployment added successfully!', 'success');
            setShowAddModal(false);
        } catch (err: any) {
            console.error('Error refreshing deployments:', err);
            showToast('❌ Failed to refresh deployments', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeploymentUpdated = async () => {
        if (!uid) return;
        setLoading(true);
        try {
            const deploymentsData = await fetchDeploymentsData(uid, isBankUser);
            setDeployments(deploymentsData);

            // Extract unique company types
            const typesSet = new Set<string>();
            deploymentsData.forEach(d => {
                if (d.company?.companyType) {
                    typesSet.add(d.company.companyType);
                }
            });
            setCompanyTypes(Array.from(typesSet));

            showToast('✅ Deployment updated successfully!', 'success');
            setShowEditModal(false);
        } catch (err: any) {
            console.error('Error refreshing deployments:', err);
            showToast('❌ Failed to refresh deployments', 'error');
        } finally {
            setLoading(false);
        }
    };

    const totalPages = Math.ceil(filtered.length / perPage);
    const paginatedData = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

    const columns: Column<Deployment>[] = [
        {
            header: 'Branch Code',
            width: '15%',
            render: (deployment) => (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                    {deployment.branch?.branchCode || 'N/A'}
                </span>
            )
        },
        {
            header: 'Branch Address',
            width: '25%',
            render: (deployment) => (
                <span className="text-sm text-gray-600 dark:text-gray-400" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                    {deployment.branch?.address || 'N/A'}
                </span>
            )
        },
        {
            header: 'Company Name',
            width: '20%',
            render: (deployment) => (
                <strong className="text-sm font-medium text-gray-900 dark:text-gray-100" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                    {deployment.company?.companyName || 'N/A'}
                </strong>
            )
        },
        {
            header: 'Company Type',
            width: '15%',
            render: (deployment) => (
                deployment.company ? (
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                        {deployment.company.companyType || 'N/A'}
                    </span>
                ) : null
            )
        },
        {
            header: 'Action',
            width: '15%',
            render: (deployment) => (
                <button
                    onClick={() => openEditModal(deployment)}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 dark:focus:ring-blue-400"
                    aria-label="Edit deployment"
                >
                    <Icon icon={FiEdit} className="h-4 w-4 mr-1" />
                    Edit
                </button>
            )
        }
    ];

    return (
        <>
            <div className="space-y-6">
                <Table
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    emptyMessage={searchTerm ? 'No deployments found matching your search' : 'No deployments available'}
                    pagination={{
                        currentPage,
                        totalPages,
                        onPageChange: setCurrentPage
                    }}
                    title="Deployments"
                    description="Manage company deployments to branches"
                    headerActions={
                        <Button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2"
                        >
                            <Icon icon={FiPlus} className="h-4 w-4" />
                            Deploy Company
                        </Button>
                    }
                    filters={
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                            <div className="relative flex-1">
                                <Icon
                                    icon={FiSearch}
                                    className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                                />
                                <input
                                    type="text"
                                    placeholder="Search by branch code, address, company name, or company ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                />
                            </div>
                            <div className="relative">
                                <select
                                    value={selectedFilter || 'All'}
                                    onChange={(e) => setSelectedFilter(e.target.value === 'All' ? null : e.target.value)}
                                    className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-4 pr-10 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                                >
                                    <option value="All">All Types</option>
                                    {companyTypes.map((type) => (
                                        <option key={type} value={type}>
                                            {type}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {searchTerm && (
                                <Button
                                    variant="secondary"
                                    onClick={handleReset}
                                    className="text-sm whitespace-nowrap"
                                >
                                    Clear Search
                                </Button>
                            )}
                        </div>
                    }
                />
            </div>

            {/* Toast */}
            {toast.show && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToast({ ...toast, show: false })}
                        />
                    </div>
                </div>
            )}

            {/* Modals */}
            <AddDeploymentModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={handleDeploymentAdded}
            />

            <EditDeploymentModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                deployment={selectedDeployment}
                onSuccess={handleDeploymentUpdated}
            />
        </>
    );
}
